"use client";

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// Abstract data-focused SVG placeholders for each division
function QuantSVG() {
  return (
    <svg width="100%" height="140" viewBox="0 0 280 140" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Quantitative equities abstract data visualization">
      <rect width="280" height="140" fill="#0A2540"/>
      {/* Grid lines */}
      {[20,40,60,80,100,120]?.map(y => (
        <line key={y} x1="0" y1={y} x2="280" y2={y} stroke="#0F4C81" strokeWidth="0.5" strokeDasharray="4 4"/>
      ))}
      {[40,80,120,160,200,240]?.map(x => (
        <line key={x} x1={x} y1="0" x2={x} y2="140" stroke="#0F4C81" strokeWidth="0.5" strokeDasharray="4 4"/>
      ))}
      {/* Yield curve line */}
      <polyline points="10,110 40,95 70,80 100,60 130,50 160,42 190,38 220,35 250,33 270,32" stroke="#A3D4FF" strokeWidth="2" fill="none" strokeLinecap="round"/>
      {/* Glow effect */}
      <polyline points="10,110 40,95 70,80 100,60 130,50 160,42 190,38 220,35 250,33 270,32" stroke="#A3D4FF" strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.15"/>
      {/* Data points */}
      {[[40,95],[100,60],[160,42],[220,35]]?.map(([cx,cy],i) => (
        <circle key={i} cx={cx} cy={cy} r="4" fill="#A3D4FF" opacity="0.9"/>
      ))}
      {/* Bar chart at bottom */}
      {[20,50,80,110,140,170,200,230,260]?.map((x,i) => {
        const h = [18,30,22,40,28,35,20,45,25]?.[i];
        return <rect key={x} x={x-8} y={140-h} width="14" height={h} fill="#4A90E2" opacity="0.25" rx="2"/>;
      })}
      {/* Label */}
      <text x="14" y="18" fill="#A3D4FF" fontSize="9" fontFamily="monospace" opacity="0.7">YIELD.CURVE.MODEL</text>
      <text x="200" y="18" fill="#4A90E2" fontSize="9" fontFamily="monospace" opacity="0.6">+12.4%</text>
    </svg>
  );
}

function MacroSVG() {
  return (
    <svg width="100%" height="140" viewBox="0 0 280 140" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Global macro forex abstract node network visualization">
      <rect width="280" height="140" fill="#0A2540"/>
      {/* Node network */}
      {/* Connections */}
      <line x1="140" y1="70" x2="60" y2="30" stroke="#0F4C81" strokeWidth="1" opacity="0.8"/>
      <line x1="140" y1="70" x2="220" y2="30" stroke="#0F4C81" strokeWidth="1" opacity="0.8"/>
      <line x1="140" y1="70" x2="40" y2="100" stroke="#0F4C81" strokeWidth="1" opacity="0.8"/>
      <line x1="140" y1="70" x2="240" y2="100" stroke="#0F4C81" strokeWidth="1" opacity="0.8"/>
      <line x1="140" y1="70" x2="140" y2="125" stroke="#0F4C81" strokeWidth="1" opacity="0.8"/>
      <line x1="60" y1="30" x2="220" y2="30" stroke="#0F4C81" strokeWidth="0.5" opacity="0.4"/>
      <line x1="40" y1="100" x2="240" y2="100" stroke="#0F4C81" strokeWidth="0.5" opacity="0.4"/>
      {/* Glow lines */}
      <line x1="140" y1="70" x2="60" y2="30" stroke="#A3D4FF" strokeWidth="1" opacity="0.3"/>
      <line x1="140" y1="70" x2="220" y2="30" stroke="#A3D4FF" strokeWidth="1" opacity="0.3"/>
      {/* Outer nodes */}
      {[[60,30],[220,30],[40,100],[240,100],[140,125]]?.map(([cx,cy],i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r="10" fill="#0A2540" stroke="#0F4C81" strokeWidth="1.5"/>
          <circle cx={cx} cy={cy} r="4" fill="#4A90E2" opacity="0.8"/>
        </g>
      ))}
      {/* Center node */}
      <circle cx="140" cy="70" r="18" fill="#0A2540" stroke="#A3D4FF" strokeWidth="1.5"/>
      <circle cx="140" cy="70" r="18" fill="#A3D4FF" opacity="0.05"/>
      <circle cx="140" cy="70" r="7" fill="#A3D4FF" opacity="0.9"/>
      {/* Currency labels */}
      <text x="50" y="28" fill="#A3D4FF" fontSize="8" fontFamily="monospace" opacity="0.7" textAnchor="middle">USD</text>
      <text x="220" y="28" fill="#A3D4FF" fontSize="8" fontFamily="monospace" opacity="0.7" textAnchor="middle">EUR</text>
      <text x="32" y="98" fill="#4A90E2" fontSize="8" fontFamily="monospace" opacity="0.7" textAnchor="middle">JPY</text>
      <text x="248" y="98" fill="#4A90E2" fontSize="8" fontFamily="monospace" opacity="0.7" textAnchor="middle">GBP</text>
      <text x="140" y="138" fill="#4A90E2" fontSize="8" fontFamily="monospace" opacity="0.7" textAnchor="middle">CHF</text>
      <text x="14" y="14" fill="#A3D4FF" fontSize="9" fontFamily="monospace" opacity="0.7">FX.FLOW.MAP</text>
    </svg>
  );
}

function OnChainSVG() {
  return (
    <svg width="100%" height="140" viewBox="0 0 280 140" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Digital assets on-chain analytics abstract blockchain visualization">
      <rect width="280" height="140" fill="#0A2540"/>
      {/* Blockchain blocks */}
      {[10,70,130,190]?.map((x,i) => (
        <g key={x}>
          <rect x={x} y="50" width="52" height="40" rx="4" fill="#0A2540" stroke="#0F4C81" strokeWidth="1.5"/>
          <rect x={x} y="50" width="52" height="40" rx="4" fill="#4A90E2" opacity="0.05"/>
          {/* Hash lines */}
          <line x1={x+8} y1="63" x2={x+44} y2="63" stroke="#A3D4FF" strokeWidth="0.8" opacity="0.4"/>
          <line x1={x+8} y1="72" x2={x+36} y2="72" stroke="#A3D4FF" strokeWidth="0.8" opacity="0.25"/>
          <line x1={x+8} y1="81" x2={x+40} y2="81" stroke="#A3D4FF" strokeWidth="0.8" opacity="0.3"/>
          {/* Chain link */}
          {i < 3 && (
            <>
              <line x1={x+52} y1="70" x2={x+70} y2="70" stroke="#A3D4FF" strokeWidth="1.5" opacity="0.6"/>
              <circle cx={x+61} cy="70" r="3" fill="#A3D4FF" opacity="0.5"/>
            </>
          )}
        </g>
      ))}
      {/* Wallet accumulation bars */}
      {[20,50,80,110,140,170,200,230,260]?.map((x,i) => {
        const h = [8,15,10,25,18,30,12,22,16]?.[i];
        return <rect key={x} x={x-6} y={105} width="10" height={h} fill="#A3D4FF" opacity="0.2" rx="1"/>;
      })}
      {/* Active block glow */}
      <rect x="190" y="50" width="52" height="40" rx="4" stroke="#A3D4FF" strokeWidth="1.5" opacity="0.6"/>
      <text x="14" y="18" fill="#A3D4FF" fontSize="9" fontFamily="monospace" opacity="0.7">ON.CHAIN.FLOW</text>
      <text x="190" y="42" fill="#A3D4FF" fontSize="8" fontFamily="monospace" opacity="0.8">LIVE</text>
      <circle cx="184" cy="39" r="3" fill="#4ade80" opacity="0.9"/>
    </svg>
  );
}

const divisions = [
  {
    id: 'quant',
    title: 'Quantitative Equities & Dividend Strategy',
    focus: 'Yield Optimization, Catalyst Forecasting, Fundamental Screening',
    approach: 'This desk is responsible for parsing global equity markets to identify high-quality dividend compounders. Combining deep fundamental analysis with algorithmic scenario-testing, the Equities team models sustainable payout ratios, free cash flow trajectories, and macroeconomic resilience to build our flagship Dividend Dominance frameworks.',
    visual: <QuantSVG />,
    tag: 'EQUITIES',
  },
  {
    id: 'macro',
    title: 'Global Macro & Forex Positioning',
    focus: 'Currency Valuations, Central Bank Divergence, Interbank Flows',
    approach: 'Navigating the FX market requires a top-down view of global liquidity. Our Macro Desk tracks sovereign bond yield spreads, algorithmic trading setups, and geopolitical catalysts. By mapping the carry trade landscape and institutional positioning, this team delivers the forward-looking intelligence necessary to hedge currency risk and capitalize on directional volatility.',
    visual: <MacroSVG />,
    tag: 'MACRO / FX',
  },
  {
    id: 'digital',
    title: 'Digital Assets & On-Chain Analytics',
    focus: 'Capital Rotation, Network Infrastructure, Institutional Flow',
    approach: 'Approaching the digital asset class with institutional skepticism and quantitative rigor. The Digital Assets team bypasses social sentiment and speculative noise, focusing strictly on verifiable on-chain metrics, smart contract utilization, and wallet accumulation patterns. They identify foundational blockchain infrastructure engineered for long-term capital capture.',
    visual: <OnChainSVG />,
    tag: 'DIGITAL ASSETS',
  },
];

export default function ResearchDeskPage() {
  return (
    <div style={{ background: '#0A2540', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
      <Header />
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 px-6 md:px-12 text-center">
        {/* Background grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.04 }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#A3D4FF" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
          </svg>
        </div>
        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(74,144,226,0.12) 0%, transparent 70%)'
        }}/>

        <div className="relative" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono mb-8"
            style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)', color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.15em' }}>
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#A3D4FF' }}/>
            INTELLIGENCE DIVISION
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
            The Research Desk.
          </h1>
          <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.75, maxWidth: '600px', margin: '0 auto' }}>
            Engineering precision intelligence through decades of institutional market experience.
          </p>
        </div>
      </section>
      {/* Divider */}
      <div className="px-6 md:px-12" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #0F4C81 30%, #0F4C81 70%, transparent)' }}/>
      </div>
      {/* Introduction Block */}
      <section className="px-6 md:px-12 py-20">
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 leading-tight"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
            The Architects of Alpha
          </h2>
          <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.7 }}>
            <p>
              At DividendChase LTD, our models are only as robust as the minds that engineer them. Our Research Desk is comprised of quantitative analysts, macroeconomic strategists, and on-chain data scientists recruited from across the institutional finance spectrum.
            </p>
            <p>
              We do not employ journalists or retail commentators. Our team operates with the strict rigor of a hedge fund intelligence unit, united by a singular mandate: to cut through market noise and deliver uncompromised, actionable data to our private subscribers and family office partners.
            </p>
          </div>
        </div>
      </section>
      {/* Divider */}
      <div className="px-6 md:px-12" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #0F4C81 30%, #0F4C81 70%, transparent)' }}/>
      </div>
      {/* Core Intelligence Divisions */}
      <section className="px-6 md:px-12 py-20">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Section header */}
          <div className="mb-14">
            <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
              SPECIALIZED UNITS
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
              Our Core Intelligence Divisions
            </h2>
          </div>

          {/* 3-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {divisions?.map((div) => (
              <div
                key={div?.id}
                className="rounded-xl overflow-hidden flex flex-col"
                style={{
                  background: '#1A2A44',
                  border: '1px solid #0F4C81',
                }}
              >
                {/* Abstract visual */}
                <div className="relative overflow-hidden" style={{ borderBottom: '1px solid #0F4C81' }}>
                  {div?.visual}
                  {/* Division tag */}
                  <div className="absolute top-3 right-3">
                    <span className="text-xs font-mono px-2 py-1 rounded"
                      style={{ background: 'rgba(10,37,64,0.85)', color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', fontSize: '9px', border: '1px solid rgba(163,212,255,0.2)' }}>
                      {div?.tag}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-bold text-white mb-3 leading-snug"
                    style={{ fontFamily: 'var(--font-display)' }}>
                    {div?.title}
                  </h3>

                  {/* Focus */}
                  <div className="mb-4">
                    <span className="text-xs font-mono uppercase tracking-widest"
                      style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em' }}>
                      Focus:
                    </span>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.55 }}>
                      {div?.focus}
                    </p>
                  </div>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'rgba(15,76,129,0.6)', marginBottom: '16px' }}/>

                  {/* Approach */}
                  <div className="flex-1">
                    <span className="text-xs font-mono uppercase tracking-widest"
                      style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.12em' }}>
                      The Approach:
                    </span>
                    <p className="text-sm mt-2 leading-relaxed" style={{ color: '#E3F2FD', opacity: 0.6 }}>
                      {div?.approach}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Divider */}
      <div className="px-6 md:px-12" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #0F4C81 30%, #0F4C81 70%, transparent)' }}/>
      </div>
      {/* Private Advisory Solutions */}
      <section className="px-6 md:px-12 py-24">
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          {/* Container card */}
          <div className="rounded-2xl p-10 md:p-14 relative overflow-hidden"
            style={{ background: '#1A2A44', border: '1px solid #0F4C81' }}>
            {/* Background glow */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse 60% 60% at 80% 20%, rgba(74,144,226,0.07) 0%, transparent 70%)'
            }}/>

            <div className="relative">
              <p className="text-xs font-mono uppercase tracking-widest mb-4"
                style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}>
                BESPOKE INTELLIGENCE
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
                style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
                Private Advisory Solutions
              </h2>
              <p className="text-base md:text-lg leading-relaxed mb-10" style={{ color: '#E3F2FD', opacity: 0.7 }}>
                For our most sophisticated clients, the expertise of the Research Desk is available on a bespoke basis. Our Private Advisory Solutions provide direct, one-on-one strategic synthesis, allowing family offices and ultra-high-net-worth investors to custom-tailor our algorithmic models to their exact capital allocation parameters.
              </p>

              <Link
                href="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-lg font-semibold text-sm transition-all duration-200 hover:opacity-90 hover:translate-y-[-1px]"
                style={{
                  background: 'linear-gradient(135deg, #4A90E2 0%, #A3D4FF 100%)',
                  color: '#0A2540',
                  fontFamily: 'var(--font-body)',
                  letterSpacing: '0.02em',
                  boxShadow: '0 4px 24px rgba(74,144,226,0.25)',
                }}
              >
                Inquire About Private Advisory
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
